import { InfoCircledIcon, TrashIcon } from '@radix-ui/react-icons'
import { NodeViewWrapper, type NodeViewProps } from '@tiptap/react'
import * as React from 'react'
import { Controlled as ControlledZoom } from 'react-medium-image-zoom'
import { cn } from '#app/utils/misc'
import { Spinner } from '../../../components/spinner'
import { blobUrlToBase64, randomId } from '../../../utils'
import { type ElementDimensions, useDragResize } from '../hooks/use-drag-resize'
import { useImageActions } from '../hooks/use-image-actions'
import { type UploadReturnType } from '../image'
import { ActionButton, ActionWrapper, ImageActions } from './image-actions'
import {
	ImageMetadataDialog,
	type ImageMetadata,
} from './image-metadata-dialog'
import { ImageOverlay } from './image-overlay'
import { ResizeHandle } from './resize-handle'

const MAX_HEIGHT = 600
const MIN_HEIGHT = 120
const MIN_WIDTH = 120

interface ImageState {
	src: string
	isServerUploading: boolean
	imageLoaded: boolean
	isZoomed: boolean
	error: boolean
	naturalSize: ElementDimensions
}

const normalizeUploadResponse = (res: UploadReturnType) => ({
	src: typeof res === 'string' ? res : res.src,
	id: typeof res === 'string' ? randomId() : res.id,
})

export const ImageViewBlock: React.FC<NodeViewProps> = ({
	editor,
	node,
	selected,
	updateAttributes,
}) => {
	const {
		src: initialSrc,
		width: initialWidth,
		height: initialHeight,
		fileName,
	} = node.attrs
	const uploadAttemptedRef = React.useRef(false)

	const initSrc = React.useMemo(() => {
		if (typeof initialSrc === 'string') {
			return initialSrc
		}
		return initialSrc.src
	}, [initialSrc])

	const [imageState, setImageState] = React.useState<ImageState>({
		src: initSrc,
		isServerUploading: false,
		imageLoaded: false,
		isZoomed: false,
		error: false,
		naturalSize: { width: initialWidth, height: initialHeight },
	})

	// Dialog state for editing metadata
	const [isMetadataDialogOpen, setIsMetadataDialogOpen] = React.useState(false)

	const containerRef = React.useRef<HTMLDivElement>(null)
	const [activeResizeHandle, setActiveResizeHandle] = React.useState<
		'left' | 'right' | null
	>(null)

	const onDimensionsChange = React.useCallback(
		({ width, height }: ElementDimensions) => {
			// Defer the update to avoid flushSync during render
			queueMicrotask(() => {
				updateAttributes({ width, height })
			})
		},
		[updateAttributes],
	)

	const aspectRatio =
		imageState.naturalSize.width / imageState.naturalSize.height
	const maxWidth = MAX_HEIGHT * aspectRatio
	const containerMaxWidth = containerRef.current
		? parseFloat(
				getComputedStyle(containerRef.current).getPropertyValue(
					'--editor-width',
				),
			)
		: Infinity

	const { isLink, onView, onDownload, onCopy, onCopyLink, onRemoveImg } =
		useImageActions({
			editor,
			node,
			src: imageState.src,
			onViewClick: (isZoomed) =>
				setImageState((prev) => ({ ...prev, isZoomed })),
		})

	const {
		currentWidth,
		currentHeight,
		updateDimensions,
		initiateResize,
		isResizing,
	} = useDragResize({
		// Only use pixel values for drag-resize, not CSS values like "100%" or "auto"
		initialWidth:
			typeof initialWidth === 'number'
				? initialWidth
				: imageState.naturalSize.width,
		initialHeight:
			typeof initialHeight === 'number'
				? initialHeight
				: imageState.naturalSize.height,
		contentWidth: imageState.naturalSize.width,
		contentHeight: imageState.naturalSize.height,
		gridInterval: 0.1,
		onDimensionsChange,
		minWidth: MIN_WIDTH,
		minHeight: MIN_HEIGHT,
		maxWidth: containerMaxWidth > 0 ? containerMaxWidth : maxWidth,
	})

	const shouldMerge = React.useMemo(() => currentWidth <= 180, [currentWidth])

	const handleImageLoad = React.useCallback(
		(ev: React.SyntheticEvent<HTMLImageElement>) => {
			const img = ev.target as HTMLImageElement
			const newNaturalSize = {
				width: img.naturalWidth,
				height: img.naturalHeight,
			}
			setImageState((prev) => ({
				...prev,
				naturalSize: newNaturalSize,
				imageLoaded: true,
			}))
			// Defer the update to avoid flushSync during image load event
			queueMicrotask(() => {
				updateAttributes({
					width: img.width || newNaturalSize.width,
					height: img.height || newNaturalSize.height,
					alt: img.alt,
					title: img.title,
				})
			})

			if (!initialWidth) {
				updateDimensions((state) => ({ ...state, width: newNaturalSize.width }))
			}
		},
		[initialWidth, updateAttributes, updateDimensions],
	)

	const handleImageError = React.useCallback(() => {
		setImageState((prev) => ({ ...prev, error: true, imageLoaded: true }))
	}, [])

	const handleResizeStart = React.useCallback(
		(direction: 'left' | 'right') =>
			(event: React.PointerEvent<HTMLDivElement>) => {
				setActiveResizeHandle(direction)
				initiateResize(direction)(event)
			},
		[initiateResize],
	)

	const handleResizeEnd = React.useCallback(() => {
		setActiveResizeHandle(null)
	}, [])

	React.useEffect(() => {
		if (!isResizing) {
			handleResizeEnd()
		}
	}, [isResizing, handleResizeEnd])

	React.useEffect(() => {
		const handleImage = async () => {
			if (!initSrc.startsWith('blob:') || uploadAttemptedRef.current) {
				return
			}

			uploadAttemptedRef.current = true
			const imageExtension = editor.options.extensions.find(
				(ext) => ext.name === 'image',
			)
			const { uploadFn } = imageExtension?.options ?? {}

			if (!uploadFn) {
				try {
					const base64 = await blobUrlToBase64(initSrc)
					setImageState((prev) => ({ ...prev, src: base64 }))
					queueMicrotask(() => {
						updateAttributes({ src: base64 })
					})
				} catch {
					setImageState((prev) => ({ ...prev, error: true }))
				}
				return
			}

			try {
				setImageState((prev) => ({ ...prev, isServerUploading: true }))
				const response = await fetch(initSrc)
				const blob = await response.blob()
				const file = new File([blob], fileName, { type: blob.type })

				const url = await uploadFn(file, editor)
				const normalizedData = normalizeUploadResponse(url)

				setImageState((prev) => ({
					...prev,
					...normalizedData,
					isServerUploading: false,
				}))

				queueMicrotask(() => {
					updateAttributes(normalizedData)
				})
			} catch {
				setImageState((prev) => ({
					...prev,
					error: true,
					isServerUploading: false,
				}))
			}
		}

		void handleImage()
	}, [editor, fileName, initSrc, updateAttributes])

	// Metadata editing handlers
	const handleEditMetadata = React.useCallback(() => {
		setIsMetadataDialogOpen(true)
	}, [])

	const handleSaveMetadata = React.useCallback(
		(data: ImageMetadata) => {
			// Keep width/height as strings to support CSS values and Tailwind classes
			updateAttributes({
				alt: data.alt && data.alt.trim() ? data.alt.trim() : null,
				title: data.title && data.title.trim() ? data.title.trim() : null,
				width: data.width && data.width.trim() ? data.width.trim() : null,
				height: data.height && data.height.trim() ? data.height.trim() : null,
				className:
					data.className && data.className.trim()
						? data.className.trim()
						: null,
			})

			setIsMetadataDialogOpen(false)
		},
		[updateAttributes],
	)

	// Current image metadata for dialog
	const currentImageData: ImageMetadata = React.useMemo(
		() => ({
			src: imageState.src,
			alt: node.attrs.alt || '',
			title: node.attrs.title || '',
			width: node.attrs.width?.toString() || '',
			height: node.attrs.height?.toString() || '',
			className: node.attrs.className || '',
		}),
		[
			imageState.src,
			node.attrs.alt,
			node.attrs.title,
			node.attrs.width,
			node.attrs.height,
			node.attrs.className,
		],
	)

	return (
		<NodeViewWrapper
			ref={containerRef}
			data-drag-handle
			className="relative text-center leading-none"
		>
			<div
				className="group/node-image relative mx-auto rounded-md object-contain"
				style={{
					maxWidth: `min(${maxWidth}px, 100%)`,
					width: currentWidth,
					maxHeight: MAX_HEIGHT,
					aspectRatio: `${imageState.naturalSize.width} / ${imageState.naturalSize.height}`,
				}}
			>
				<div
					className={cn(
						'relative flex h-full cursor-default flex-col items-center gap-2 rounded',
						{
							'outline-primary outline-2 outline-offset-1':
								selected || isResizing,
						},
					)}
				>
					<div className="h-full contain-paint">
						<div className="relative h-full">
							{imageState.isServerUploading && !imageState.error && (
								<div className="absolute inset-0 flex items-center justify-center">
									<Spinner className="size-7" />
								</div>
							)}

							{imageState.error && (
								<div className="absolute inset-0 flex flex-col items-center justify-center">
									<InfoCircledIcon className="text-destructive size-8" />
									<p className="text-muted-foreground mt-2 text-sm">
										Failed to load image
									</p>
								</div>
							)}

							<ControlledZoom
								isZoomed={imageState.isZoomed}
								onZoomChange={() =>
									setImageState((prev) => ({ ...prev, isZoomed: false }))
								}
							>
								<img
									className={cn(
										'h-auto rounded object-contain transition-shadow',
										{
											'opacity-0': !imageState.imageLoaded || imageState.error,
										},
										// Apply Tailwind classes if they're detected
										typeof node.attrs.width === 'string' &&
											node.attrs.width.startsWith('w-')
											? node.attrs.width
											: '',
										typeof node.attrs.height === 'string' &&
											node.attrs.height.startsWith('h-')
											? node.attrs.height
											: '',
										// Apply custom classes from user
										node.attrs.className || '',
									)}
									style={{
										maxWidth: `min(100%, ${maxWidth}px)`,
										minWidth: `${MIN_WIDTH}px`,
										maxHeight: MAX_HEIGHT,
										// Apply CSS values (not Tailwind classes)
										...(node.attrs.width &&
											typeof node.attrs.width === 'string' &&
											!node.attrs.width.startsWith('w-') && {
												width: node.attrs.width,
											}),
										...(node.attrs.height &&
											typeof node.attrs.height === 'string' &&
											!node.attrs.height.startsWith('h-') && {
												height: node.attrs.height,
											}),
									}}
									// Only use HTML attributes for drag-resize when not using CSS values
									{...(typeof node.attrs.width !== 'string' && {
										width: currentWidth,
									})}
									{...(typeof node.attrs.height !== 'string' && {
										height: currentHeight,
									})}
									src={imageState.src}
									onError={handleImageError}
									onLoad={handleImageLoad}
									alt={node.attrs.alt || ''}
									title={node.attrs.title || ''}
									id={node.attrs.id}
								/>
							</ControlledZoom>
						</div>

						{imageState.isServerUploading && <ImageOverlay />}

						{editor.isEditable &&
							imageState.imageLoaded &&
							!imageState.error &&
							!imageState.isServerUploading && (
								<>
									<ResizeHandle
										onPointerDown={handleResizeStart('left')}
										className={cn('left-1', {
											hidden: isResizing && activeResizeHandle === 'right',
										})}
										isResizing={isResizing && activeResizeHandle === 'left'}
									/>
									<ResizeHandle
										onPointerDown={handleResizeStart('right')}
										className={cn('right-1', {
											hidden: isResizing && activeResizeHandle === 'left',
										})}
										isResizing={isResizing && activeResizeHandle === 'right'}
									/>
								</>
							)}
					</div>

					{imageState.error && (
						<ActionWrapper>
							<ActionButton
								icon={<TrashIcon />}
								tooltip="Remove image"
								onClick={onRemoveImg}
							/>
						</ActionWrapper>
					)}

					{!isResizing &&
						!imageState.error &&
						!imageState.isServerUploading && (
							<ImageActions
								shouldMerge={shouldMerge}
								isLink={isLink}
								onView={onView}
								onDownload={onDownload}
								onCopy={onCopy}
								onCopyLink={onCopyLink}
								onEditMetadata={handleEditMetadata}
							/>
						)}
				</div>
			</div>

			{/* Image Metadata Dialog */}
			<ImageMetadataDialog
				open={isMetadataDialogOpen}
				onOpenChange={setIsMetadataDialogOpen}
				initialData={currentImageData}
				onSave={handleSaveMetadata}
			/>
		</NodeViewWrapper>
	)
}
